import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as TemplateAPI from 'api/Template';
import { dispatch } from 'store';
import { TYPE_OF_FIELD_IN_COLUMN } from 'utils/scorecard';
import { openSnackbar } from 'store/reducers/snackbar';
import { PAGES } from 'sections/Template/constants';

const displayMessage = ({ type, msg }) => {
  dispatch(
    openSnackbar({
      open: true,
      message: msg ? msg : type === 'success' ? 'Success' : 'Something went wrong',
      variant: 'alert',
      alert: {
        color: type || 'success'
      },
      close: false
    })
  );
};

const initialState = {
  selectedPage: PAGES.MAIN_PAGE,
  templatePoint: {
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    column: [
      { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Name', key: 'name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Data Type', key: 'datatype', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
    ],
    data: null
  },
  useCase: {
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    column: [
      { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Name', key: 'name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Scope', key: 'scope', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Template Points', key: 'template_points', field_type: TYPE_OF_FIELD_IN_COLUMN.ARRAY, width: 210 },
      { title: 'Subscription Plans', key: 'subscription_plan_type', field_type: TYPE_OF_FIELD_IN_COLUMN.ARRAY, width: 120 },
      { title: 'Action', key: 'Action', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
    ],
    data: null,
    allTemplatePoints: []
  },
  customerUseCase: {
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    column: [
      { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Name', key: 'agent_core_usecase.name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Scope', key: 'agent_core_usecase.scope', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Template Points', key: 'agent_core_usecase.template_points', field_type: TYPE_OF_FIELD_IN_COLUMN.ARRAY, width: 210 },
      { title: 'VDB', key: 'vdb', field_type: TYPE_OF_FIELD_IN_COLUMN.BOOLEAN, width: 120 },
      { title: 'VDB URL', key: 'vdb_data.vdb_url', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Collection Name', key: 'vdb_data.collection_name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Threshold', key: 'vdb_data.threshold', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Words Limit', key: 'vdb_data.wordsLimit', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      {
        title: 'Subscription Plans',
        key: 'agent_core_usecase.subscription_plan_type',
        field_type: TYPE_OF_FIELD_IN_COLUMN.ARRAY,
        width: 120
      },
      { title: 'Status', key: 'status', field_type: TYPE_OF_FIELD_IN_COLUMN.STATUS, width: 120 },
      { title: 'Action', key: 'Action', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
    ],
    data: null,
    allPublicUsecases: [],
    allLLMVendors: [],
    allLLmVendorModels: []
  },
  templateValue: {
    selectedUseCaseId: null,
    data: null
  },
  status: '',
  ui: {
    loading: false,
    msg: ''
  }
};

const getTemplatePointData = createAsyncThunk('template/getTemplatePointData', async ({ params, signal }) => {
  const res = await TemplateAPI.getTemplatePointData(params, signal);
  return res?.data?.data;
});

const createTemplatePoint = createAsyncThunk('template/createTemplatePoint', async (params) => {
  try {
    const res = await TemplateAPI.createTemplatePoint(params);
    displayMessage({ type: 'success', msg: 'Template point created successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateTemplatePoint = createAsyncThunk('template/updateTemplatePoint', async (params) => {
  try {
    const res = await TemplateAPI.updateTemplatePoint(params);
    displayMessage({ type: 'success', msg: 'Template point updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteTemplatePoint = createAsyncThunk('template/deleteTemplatePoint', async (id) => {
  try {
    const res = await TemplateAPI.deleteTemplatePoint(id);
    displayMessage({ type: 'success', msg: 'Template point deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const getUseCaseData = createAsyncThunk('template/getUseCaseData', async ({ params, signal }) => {
  const res = await TemplateAPI.getUseCaseData(params, signal);
  return res?.data?.data;
});

const createUseCase = createAsyncThunk('template/createUseCase', async (params) => {
  try {
    const res = await TemplateAPI.createUseCase(params);
    displayMessage({ type: 'success', msg: 'Usecase created successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateUseCase = createAsyncThunk('template/updateUseCase', async (params) => {
  try {
    const res = await TemplateAPI.updateUseCase(params);
    displayMessage({ type: 'success', msg: 'Usecase updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteUseCase = createAsyncThunk('template/deleteUseCase', async (id) => {
  try {
    const res = await TemplateAPI.deleteUseCase(id);
    displayMessage({ type: 'success', msg: 'Usecase deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const getAllTemplatePoints = createAsyncThunk('template/getAllTemplatePoints', async () => {
  const res = await TemplateAPI.getAllTemplatePoints();
  return res?.data?.data;
});

const getTemplateValuesData = createAsyncThunk('template/getTemplateValuesData', async (params) => {
  const res = await TemplateAPI.getTemplateValuesData(params);
  return res?.data?.data;
});

const updateTemplateValues = createAsyncThunk('template/updateTemplateValues', async (params) => {
  try {
    const res = await TemplateAPI.updateTemplateValues(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

//Customer
const getCustomerUseCaseData = createAsyncThunk('template/getCustomerUseCaseData', async ({ params, signal }) => {
  const res = await TemplateAPI.getCustomerUseCaseData(params, signal);
  return res?.data?.data;
});

const createCustomerUseCase = createAsyncThunk('template/createCustomerUseCase', async (params) => {
  try {
    const res = await TemplateAPI.createCustomerUseCase(params);
    displayMessage({ type: 'success', msg: 'Usecase created successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateCustomerUseCase = createAsyncThunk('template/updateCustomerUseCase', async (params) => {
  try {
    const res = await TemplateAPI.updateCustomerUseCase(params);
    displayMessage({ type: 'success', msg: 'Usecase updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteCustomerUseCase = createAsyncThunk('template/deleteCustomerUseCase', async (id) => {
  try {
    const res = await TemplateAPI.deleteCustomerUseCase(id);
    displayMessage({ type: 'success', msg: 'Usecase deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const getAllPublicUseCases = createAsyncThunk('template/getAllPublicUseCases', async () => {
  const res = await TemplateAPI.getAllPublicUseCases();
  return res?.data?.data;
});

const getAllLLMVendors = createAsyncThunk('template/getAllLLMVendors', async () => {
  const res = await TemplateAPI.getAllLLMVendors();
  return res?.data?.data;
});

const getAllLLMVendorModels = createAsyncThunk('template/getAllLLMVendorModels', async (params) => {
  try {
    const res = await TemplateAPI.getAllLLMVendorModels(params);
    return res?.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const TemplateSlice = createSlice({
  name: 'template',
  initialState: initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },
    setSelectedPage: (state, action) => {
      state.selectedPage = action.payload;
    },
    //Template Point Tab
    setTemplatePointCurrentPage: (state, action) => {
      state.templatePoint.pagination.currentPage = action.payload;
    },
    setTemplatePointPageSize: (state, action) => {
      state.templatePoint.pagination.pageSize = action.payload;
    },

    //UseCase Tab
    setUseCaseCurrentPage: (state, action) => {
      state.useCase.pagination.currentPage = action.payload;
    },
    setUseCasePageSize: (state, action) => {
      state.useCase.pagination.pageSize = action.payload;
    },

    //Customer UseCase
    setCustUseCaseCurrentPage: (state, action) => {
      state.customerUseCase.pagination.currentPage = action.payload;
    },
    setCustUseCasePageSize: (state, action) => {
      state.customerUseCase.pagination.pageSize = action.payload;
    },

    //Template value
    setSelectedUseCaseId: (state, action) => {
      state.templateValue.selectedUseCaseId = action.payload;
    },
    removeSelectedUseCase: (state) => {
      state.templateValue.selectedUseCaseId = null;
    }
  },
  extraReducers: {
    [getAllTemplatePoints.fulfilled]: (state, action) => {
      state.useCase.allTemplatePoints = action.payload;
    },
    [getAllPublicUseCases.fulfilled]: (state, action) => {
      state.customerUseCase.allPublicUsecases = action.payload;
    },
    [getAllLLMVendors.fulfilled]: (state, action) => {
      state.customerUseCase.allLLMVendors = action.payload;
    },
    [getAllLLMVendorModels.fulfilled]: (state, action) => {
      state.customerUseCase.allLLmVendorModels = action.payload;
    },
    [getTemplatePointData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getTemplatePointData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.templatePoint.pagination.totalRecords = action.payload?.count;
      state.templatePoint.data = action.payload?.results || [];
    },
    [getTemplatePointData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createTemplatePoint.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [createTemplatePoint.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createTemplatePoint.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [updateTemplatePoint.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateTemplatePoint.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      const updatedData = state.templatePoint.data.map((item) => {
        if (item.id === action.payload.id) {
          return action.payload;
        }
        return item;
      });
      state.templatePoint.data = updatedData;
    },

    [updateTemplatePoint.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [deleteTemplatePoint.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [deleteTemplatePoint.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [deleteTemplatePoint.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [getUseCaseData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getUseCaseData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.useCase.pagination.totalRecords = action.payload?.count;
      state.useCase.data = action.payload?.results || [];
    },
    [getUseCaseData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createUseCase.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [createUseCase.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createUseCase.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [updateUseCase.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateUseCase.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [updateUseCase.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [deleteUseCase.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [deleteUseCase.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [deleteUseCase.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [getTemplateValuesData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getTemplateValuesData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.templateValue.data = action.payload;
    },
    [getTemplateValuesData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },

    [updateTemplateValues.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateTemplateValues.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [updateTemplateValues.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [getCustomerUseCaseData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getCustomerUseCaseData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.customerUseCase.pagination.totalRecords = action.payload?.count;
      state.customerUseCase.data = action.payload?.results || [];
    },
    [getCustomerUseCaseData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createCustomerUseCase.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [createCustomerUseCase.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createCustomerUseCase.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [updateCustomerUseCase.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateCustomerUseCase.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [updateCustomerUseCase.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [deleteCustomerUseCase.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [deleteCustomerUseCase.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [deleteCustomerUseCase.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    }
  }
});

const {
  resetState,
  setSelectedPage,
  setTemplatePointCurrentPage,
  setTemplatePointPageSize,
  setUseCaseCurrentPage,
  setUseCasePageSize,
  setSelectedUseCaseId,
  removeSelectedUseCase,
  setCustUseCaseCurrentPage,
  setCustUseCasePageSize
} = TemplateSlice.actions;

export default TemplateSlice.reducer;

export {
  resetState,
  setSelectedPage,
  //Template Point
  setTemplatePointCurrentPage,
  setTemplatePointPageSize,
  getTemplatePointData,
  createTemplatePoint,
  updateTemplatePoint,
  deleteTemplatePoint,
  //UseCase
  setUseCaseCurrentPage,
  setUseCasePageSize,
  getUseCaseData,
  createUseCase,
  updateUseCase,
  deleteUseCase,
  getAllTemplatePoints,
  setSelectedUseCaseId,
  //TemplateValues
  removeSelectedUseCase,
  getTemplateValuesData,
  updateTemplateValues,

  //Cutomer Usecase
  setCustUseCaseCurrentPage,
  setCustUseCasePageSize,
  getAllPublicUseCases,
  getCustomerUseCaseData,
  createCustomerUseCase,
  updateCustomerUseCase,
  deleteCustomerUseCase,
  getAllLLMVendors,
  getAllLLMVendorModels
};
